import { useRef, useState } from "react";
import type { RuleSet } from "@sportsos/engine";
import type { Latch, LatchKey } from "../match/entry.ts";
import { anyLatched, isLatched, latchBlockedReason, latchLabel } from "../match/entry.ts";
import { wagonModeLabel, type WagonMode } from "../match/wagonPrefs.ts";
import {
  beganScrub,
  HOLD_MS,
  NO_SCRUB,
  releasedAt,
  scrubLabel,
  scrubbedTo,
  type Scrub,
} from "../match/scrub.ts";

const MODIFIERS: readonly { readonly key: LatchKey; readonly label: string }[] = [
  { key: "wicket", label: "W" },
  { key: "wide", label: "WD" },
  { key: "noball", label: "NB" },
  { key: "bye", label: "B" },
  { key: "legbye", label: "LB" },
];

interface Props {
  readonly latch: Latch;
  readonly ruleSet: RuleSet;
  readonly freeHit: boolean;
  /** The wagon wheel setting for this match (ADR-0022), switchable for the whole match. */
  readonly wagon?: WagonMode;
  readonly onWagon?: () => void;
  readonly canUndo: boolean;
  readonly undoableBalls: number;
  readonly onLatch: (key: LatchKey) => void;
  readonly onClearLatch: () => void;
  readonly onRuns: (runs: number) => void;
  readonly onRetire: () => void;
  readonly onChangeBowler: () => void;
  readonly onLedger: () => void;
  readonly onChangeKeeper: () => void;
  readonly onUndo: () => void;
  readonly onUndoMany: (count: number) => void;
  /**
   * Whether the batters who just came in can still change ends: before the first ball, or
   * after a wicket before the next. The key is always there and only disabled otherwise, so
   * the tap half keeps its shape between balls.
   */
  readonly canSwap?: boolean;
  readonly onSwap?: () => void;
  /** Shown at the top of the pad, such as the Openers strip before the first ball. */
  readonly top?: React.ReactNode;
}

const KEY_H = 48;
const SM_KEY_H = 38;

export function Pad({
  latch,
  ruleSet,
  freeHit,
  wagon = "off",
  onWagon = () => undefined,
  canUndo,
  undoableBalls,
  onLatch,
  onClearLatch,
  onRuns,
  onRetire,
  onChangeBowler,
  onLedger,
  onChangeKeeper,
  onUndo,
  onUndoMany,
  canSwap = false,
  onSwap = () => undefined,
  top = null,
}: Props): React.ReactElement {
  const [scrub, setScrub] = useState<Scrub>(NO_SCRUB);
  const origin = useRef(0);
  const held = useRef(0);

  const label = latchLabel(latch);

  return (
    <div className="ds-sheet" style={{ padding: "var(--ds-space-3) var(--ds-space-3) var(--ds-space-4)" }}>
      {top}
      {/* Latch banner */}
      {anyLatched(latch) ? (
        <div className="row" style={{ marginBottom: "var(--ds-space-2)", gap: "var(--ds-space-2)" }}>
          <span className={`ds-tag${latch.wicket ? " ds-tag--live" : ""}`} style={latch.wicket ? undefined : { background: "var(--ds-cat-amber-fill)", color: "var(--ds-cat-amber-ink)" }}>
            {label}
          </span>
          <span className="ds-caption" style={{ flex: 1 }}>
            {latch.wicket ? "tap runs for wicket" : "tap runs"}
          </span>
          <button className="ds-btn ds-btn--sm ds-btn--ghost" onClick={onClearLatch} style={{ minHeight: 28, padding: "0 var(--ds-space-2)" }}>
            Clear
          </button>
        </div>
      ) : (
        // One row in both states, so switching the wheel on or off never moves a key. The switch
        // is here for the whole match (ADR-0022): a scorer who forgot to turn it on at the start can
        // turn it on in the third over, and balls from then on carry a direction.
        <div className="row" style={{ margin: "0 0 var(--ds-space-2)", justifyContent: "space-between", minHeight: 28 }}>
          <span style={{ fontSize: "var(--ds-text-2xs)", color: "var(--ds-text-muted)" }}>
            {freeHit ? "Free hit — run out only" : "Runs off the bat"}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={wagon === "on"}
            className="ds-btn ds-btn--sm ds-btn--ghost"
            // A border, so it reads as a control and not as a caption. Tinted when on, by colour
            // alone: its size is the same either way, so no key can move.
            style={{
              minHeight: 28,
              padding: "0 var(--ds-space-2)",
              fontSize: "var(--ds-text-2xs)",
              borderRadius: "var(--ds-radius-sm)",
              border: `1px solid ${wagon === "on" ? "var(--ds-accent)" : "var(--ds-border-strong)"}`,
              background: wagon === "on" ? "var(--ds-accent-subtle)" : undefined,
            }}
            onClick={onWagon}
          >
            Wagon wheel: <b>{wagonModeLabel(wagon)}</b>
          </button>
        </div>
      )}

      {/* Main run keys: 0 1 2 3 · 4 6 · WICKET */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "var(--ds-space-1)" }}>
        {[0, 1, 2, 3].map((r) => (
          <button key={r} className="ds-key" style={{ height: KEY_H, fontSize: "1.25rem" }} onClick={() => onRuns(r)}>{r}</button>
        ))}
        <button className="ds-key ds-key--teal" style={{ height: KEY_H, fontSize: "1.25rem" }} onClick={() => onRuns(4)}>4</button>
        <button className="ds-key ds-key--violet" style={{ height: KEY_H, fontSize: "1.25rem" }} onClick={() => onRuns(6)}>6</button>
        <button
          className="ds-key ds-key--magenta ds-key--wide"
          style={{ height: KEY_H, fontSize: "var(--ds-text-sm)", letterSpacing: "var(--ds-tracking-label)" }}
          onClick={() => onLatch("wicket")}
          aria-pressed={latch.wicket}
        >
          WICKET
        </button>
      </div>

      {/* Extras row: WD NB B LB + rare runs 5 7 */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "var(--ds-space-1)", marginTop: "var(--ds-space-1)" }}>
        {MODIFIERS.filter(({ key }) => key !== "wicket").map(({ key, label: text }) => {
          const blocked = latchBlockedReason(latch, key, ruleSet);
          const on = isLatched(latch, key);
          return (
            <button
              key={key}
              className={`ds-key ds-key--sm${on ? " ds-key--primary" : " ds-key--amber"}`}
              style={{ height: SM_KEY_H, fontSize: "var(--ds-text-2xs)", fontFamily: "var(--ds-font-sans)" }}
              onClick={() => onLatch(key)}
              disabled={blocked !== null}
              title={blocked ?? undefined}
              aria-pressed={on}
            >
              {text}
            </button>
          );
        })}
        <button className="ds-key ds-key--sm" style={{ height: SM_KEY_H, fontSize: "var(--ds-text-2xs)" }} onClick={() => onRuns(5)}>5</button>
        <button className="ds-key ds-key--sm" style={{ height: SM_KEY_H, fontSize: "var(--ds-text-2xs)" }} onClick={() => onRuns(7)}>7</button>
      </div>

      {/* Action bar: Undo · Retire · Bowler · Keeper · Ledger · Swap */}
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1fr 1fr 1fr", gap: "var(--ds-space-1)", marginTop: "var(--ds-space-2)" }}>
        <button
          className="ds-key ds-key--sm ds-key--primary undo-btn"
          style={{ height: SM_KEY_H, fontSize: "var(--ds-text-2xs)", fontFamily: "var(--ds-font-sans)" }}
          disabled={!canUndo}
          onClick={() => { if (!scrub.active) onUndo(); }}
          onPointerDown={(event) => {
            origin.current = event.clientX;
            held.current = window.setTimeout(() => setScrub(beganScrub()), HOLD_MS);
          }}
          onPointerMove={(event) =>
            setScrub((current) => scrubbedTo(current, event.clientX - origin.current, undoableBalls))
          }
          onPointerUp={() => {
            window.clearTimeout(held.current);
            const count = releasedAt(scrub, undoableBalls);
            setScrub(NO_SCRUB);
            if (count !== null) onUndoMany(count);
          }}
          onPointerLeave={() => {
            window.clearTimeout(held.current);
            setScrub(NO_SCRUB);
          }}
        >
          {scrub.active ? scrubLabel(scrub.count) : "Undo"}
        </button>
        <button className="ds-key ds-key--sm" style={{ height: SM_KEY_H, fontSize: "var(--ds-text-2xs)", fontFamily: "var(--ds-font-sans)" }} onClick={onRetire}>Retire</button>
        <button className="ds-key ds-key--sm" style={{ height: SM_KEY_H, fontSize: "var(--ds-text-2xs)", fontFamily: "var(--ds-font-sans)" }} onClick={onChangeBowler}>Bowler</button>
        <button className="ds-key ds-key--sm" style={{ height: SM_KEY_H, fontSize: "var(--ds-text-2xs)", fontFamily: "var(--ds-font-sans)" }} onClick={onChangeKeeper}>Keeper</button>
        <button className="ds-key ds-key--sm" style={{ height: SM_KEY_H, fontSize: "var(--ds-text-2xs)", fontFamily: "var(--ds-font-sans)" }} onClick={onLedger}>Ledger</button>
        <button
          className="ds-key ds-key--sm"
          style={{ height: SM_KEY_H, fontSize: "var(--ds-text-2xs)", fontFamily: "var(--ds-font-sans)" }}
          aria-label="Swap batters"
          disabled={!canSwap}
          onClick={onSwap}
        >
          ⇄ Swap
        </button>
      </div>
    </div>
  );
}
